import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip,
  Paper
} from '@mui/material';
import {
  Add as AddIcon,
  LocationOn as LocationIcon,
  Map as MapIcon,
  Build as BuildIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config/api';

interface MapImage {
  filename: string;
  url: string;
  name: string;
}

interface InfrastructureRequest {
  id: string;
  title: string;
  description: string;
  category: 'maintenance' | 'equipment' | 'facility' | 'safety' | 'technology' | 'other';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'pending' | 'accepted' | 'in_progress' | 'completed' | 'cancelled';
  requestedDate: string;
  location: string | null;
  department: string | null;
  mapCoordinates: { x: number; y: number } | null;
  mapImage: string | null;
  adminNotes: string | null;
  estimatedCost: number | null;
  actualCost: number | null;
  requester: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

const InfrastructureMaps: React.FC = () => {
  const { user, token } = useAuth();
  const [mapImages, setMapImages] = useState<MapImage[]>([]);
  const [selectedMap, setSelectedMap] = useState<string>('Msala.png');
  const [mapRequests, setMapRequests] = useState<InfrastructureRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newRequestForm, setNewRequestForm] = useState({
    title: '',
    description: '',
    category: 'other' as const,
    priority: 'medium' as const,
    location: '',
    department: '',
    mapCoordinates: { x: 0, y: 0 },
    mapImage: ''
  });
  const mapRef = useRef<HTMLDivElement>(null);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 });
  const [ticketDetailsDialogOpen, setTicketDetailsDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<InfrastructureRequest | null>(null);

  // Coordinate mapping functions for accurate pin positioning
  const getDisplayCoordinates = (storedX: number, storedY: number) => {
    if (!mapRef.current || imageDimensions.width === 0 || imageDimensions.height === 0) {
      return { x: 0, y: 0 };
    }

    const container = mapRef.current;
    const containerRect = container.getBoundingClientRect();
    const containerAspectRatio = containerRect.width / containerRect.height;
    const imageAspectRatio = imageDimensions.width / imageDimensions.height;

    let displayWidth, displayHeight, offsetX, offsetY;

    if (containerAspectRatio > imageAspectRatio) {
      // Container is wider than image - image fits height, letterboxed horizontally
      displayHeight = containerRect.height;
      displayWidth = displayHeight * imageAspectRatio;
      offsetX = (containerRect.width - displayWidth) / 2;
      offsetY = 0;
    } else {
      // Container is taller than image - image fits width, letterboxed vertically
      displayWidth = containerRect.width;
      displayHeight = displayWidth / imageAspectRatio;
      offsetX = 0;
      offsetY = (containerRect.height - displayHeight) / 2;
    }

    // Convert stored coordinates (0-1) to display coordinates
    const displayX = offsetX + (storedX * displayWidth);
    const displayY = offsetY + (storedY * displayHeight);

    return {
      x: (displayX / containerRect.width) * 100,
      y: (displayY / containerRect.height) * 100
    };
  };

  const getStoredCoordinates = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!mapRef.current || imageDimensions.width === 0 || imageDimensions.height === 0) {
      return { x: 0, y: 0 };
    }

    const container = mapRef.current;
    const containerRect = container.getBoundingClientRect();
    const containerAspectRatio = containerRect.width / containerRect.height;
    const imageAspectRatio = imageDimensions.width / imageDimensions.height;

    let displayWidth, displayHeight, offsetX, offsetY;

    if (containerAspectRatio > imageAspectRatio) {
      // Container is wider than image - image fits height, letterboxed horizontally
      displayHeight = containerRect.height;
      displayWidth = displayHeight * imageAspectRatio;
      offsetX = (containerRect.width - displayWidth) / 2;
      offsetY = 0;
    } else {
      // Container is taller than image - image fits width, letterboxed vertically
      displayWidth = containerRect.width;
      displayHeight = displayWidth / imageAspectRatio;
      offsetX = 0;
      offsetY = (containerRect.height - displayHeight) / 2;
    }

    const clickX = event.clientX - containerRect.left;
    const clickY = event.clientY - containerRect.top;

    // Check if click is within the actual image bounds
    if (clickX < offsetX || clickX > offsetX + displayWidth || 
        clickY < offsetY || clickY > offsetY + displayHeight) {
      return null; // Click outside image bounds
    }

    // Convert display coordinates to stored coordinates (0-1)
    const storedX = (clickX - offsetX) / displayWidth;
    const storedY = (clickY - offsetY) / displayHeight;

    return { x: storedX, y: storedY };
  };

  // Fetch available map images
  const fetchMapImages = async () => {
    try {
      console.log('🗺️ Fetching map images from:', `${API_BASE_URL}/api/infrastructure/maps`);
      const response = await fetch(`${API_BASE_URL}/api/infrastructure/maps`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch map images');
      }

      const data = await response.json();
      console.log('🗺️ Map images response:', data);
      setMapImages(data.maps);
      
      // Auto-select Msala.png if available (building schema)
      const msala = data.maps.find((map: MapImage) => map.filename === 'Msala.png');
      console.log('🗺️ Msala map found:', msala);
      if (msala) {
        setSelectedMap('Msala.png');
      }
    } catch (err) {
      console.error('Error fetching map images:', err);
      setError('Failed to load map images');
    }
  };

  // Fetch requests for selected map
  const fetchMapRequests = async (mapImage: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/infrastructure/map/${mapImage}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch map requests');
      }

      const data = await response.json();
      setMapRequests(data.requests);
    } catch (err) {
      console.error('Error fetching map requests:', err);
      setError('Failed to load map requests');
    }
  };

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchMapImages();
      setLoading(false);
    };
    loadData();
  }, [token]);

  // Fetch requests when map is selected
  useEffect(() => {
    if (selectedMap) {
      fetchMapRequests(selectedMap);
    }
  }, [selectedMap, token]);

  // Handle window resize to recalculate pin positions
  useEffect(() => {
    const handleResize = () => {
      // Force re-render by updating a dummy state
      setContainerDimensions(prev => ({ ...prev }));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle map click
  const handleMapClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!selectedMap || !mapRef.current) return;

    const coordinates = getStoredCoordinates(event);
    if (!coordinates) return; // Click outside image bounds

    setNewRequestForm({
      ...newRequestForm,
      mapCoordinates: coordinates,
      mapImage: selectedMap
    });

    setCreateDialogOpen(true);
  };

  // Handle request creation
  const handleCreateRequest = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/infrastructure`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...newRequestForm,
          requestedDate: new Date().toISOString()
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create request');
      }

      // Refresh map requests
      if (selectedMap) {
        await fetchMapRequests(selectedMap);
      }

      setCreateDialogOpen(false);
      setNewRequestForm({
        title: '',
        description: '',
        category: 'other',
        priority: 'medium',
        location: '',
        department: '',
        mapCoordinates: { x: 0, y: 0 },
        mapImage: ''
      });
    } catch (err) {
      console.error('Error creating request:', err);
      setError('Failed to create request');
    }
  };

  // Get priority color
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'accepted': return 'info';
      case 'in_progress': return 'primary';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  // Get category label
  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'maintenance': return 'Priežiūra';
      case 'equipment': return 'Įranga';
      case 'facility': return 'Patalpos';
      case 'safety': return 'Sauga';
      case 'technology': return 'Technologijos';
      case 'other': return 'Kita';
      default: return category;
    }
  };

  // Handle ticket marker click
  const handleTicketClick = (ticket: InfrastructureRequest) => {
    setSelectedTicket(ticket);
    setTicketDetailsDialogOpen(true);
  };

  // Close ticket details dialog
  const handleCloseTicketDetails = () => {
    setTicketDetailsDialogOpen(false);
    setSelectedTicket(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Infrastruktūros Žemėlapiai
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Map Display */}
      <Box sx={{ width: '100%' }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Pastato Žemėlapis - Msala
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              <InfoIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
              Dukart spustelėkite žemėlapyje, kad sukurtumėte naują infrastruktūros prašymą
            </Typography>

            {selectedMap ? (
              <Box
                ref={mapRef}
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: '50vh',
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  overflow: 'hidden',
                  cursor: 'crosshair'
                }}
                onDoubleClick={handleMapClick}
              >
                <img
                  src={`${API_BASE_URL}/uploads/${selectedMap}`}
                  alt="Map"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    userSelect: 'none'
                  }}
                  onLoad={(e) => {
                    console.log('🗺️ Map image loaded:', selectedMap);
                    const img = e.target as HTMLImageElement;
                    setImageDimensions({
                      width: img.naturalWidth,
                      height: img.naturalHeight
                    });
                  }}
                  onError={(e) => {
                    console.error('🗺️ Map image failed to load:', selectedMap, e);
                  }}
                />

                {/* Render request markers */}
                {mapRequests.map((request) => {
                  if (!request.mapCoordinates) return null;
                  
                  const displayCoords = getDisplayCoordinates(request.mapCoordinates.x, request.mapCoordinates.y);
                  const x = displayCoords.x;
                  const y = displayCoords.y;

                  return (
                    <Tooltip
                      key={request.id}
                      title={
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                            {request.title}
                          </Typography>
                          <Typography variant="body2">
                            {request.description}
                          </Typography>
                          <Typography variant="caption" display="block">
                            {request.requester.firstName} {request.requester.lastName}
                          </Typography>
                        </Box>
                      }
                      arrow
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          left: `${x}%`,
                          top: `${y}%`,
                          transform: 'translate(-50%, -100%)',
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center'
                        }}
                        onClick={() => handleTicketClick(request)}
                      >
                        <Chip
                          icon={<LocationIcon />}
                          label={request.title}
                          color={getPriorityColor(request.priority) as any}
                          size="small"
                          sx={{
                            maxWidth: '120px',
                            '& .MuiChip-label': {
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }
                          }}
                        />
                        {/* Arrow pointer pointing down to exact location */}
                        <Box
                          sx={{
                            width: 0,
                            height: 0,
                            borderLeft: '6px solid transparent',
                            borderRight: '6px solid transparent',
                            borderTop: `8px solid ${
                              getPriorityColor(request.priority) === 'error' ? '#f44336' :
                              getPriorityColor(request.priority) === 'warning' ? '#ff9800' :
                              getPriorityColor(request.priority) === 'info' ? '#2196f3' :
                              getPriorityColor(request.priority) === 'success' ? '#4caf50' :
                              '#9e9e9e'
                            }`,
                            marginTop: '-1px'
                          }}
                        />
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '50vh',
                  border: '2px dashed #ddd',
                  borderRadius: 1,
                  backgroundColor: '#f5f5f5'
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  Pasirinkite žemėlapį
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Create Request Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Sukurti Naują Infrastruktūros Prašymą
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Pavadinimas"
            value={newRequestForm.title}
            onChange={(e) => setNewRequestForm({ ...newRequestForm, title: e.target.value })}
            sx={{ mb: 2 }}
            required
          />
          
          <TextField
            fullWidth
            label="Aprašymas"
            multiline
            rows={3}
            value={newRequestForm.description}
            onChange={(e) => setNewRequestForm({ ...newRequestForm, description: e.target.value })}
            sx={{ mb: 2 }}
            required
          />
          
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Box sx={{ flex: 1 }}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Kategorija</InputLabel>
                <Select
                  value={newRequestForm.category}
                  onChange={(e) => setNewRequestForm({ ...newRequestForm, category: e.target.value as any })}
                  label="Kategorija"
                >
                  <MenuItem value="maintenance">Priežiūra</MenuItem>
                  <MenuItem value="equipment">Įranga</MenuItem>
                  <MenuItem value="facility">Pastatas</MenuItem>
                  <MenuItem value="safety">Saugumas</MenuItem>
                  <MenuItem value="technology">Technologijos</MenuItem>
                  <MenuItem value="other">Kita</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            <Box sx={{ flex: 1 }}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Prioritetas</InputLabel>
                <Select
                  value={newRequestForm.priority}
                  onChange={(e) => setNewRequestForm({ ...newRequestForm, priority: e.target.value as any })}
                  label="Prioritetas"
                >
                  <MenuItem value="low">Žemas</MenuItem>
                  <MenuItem value="medium">Vidutinis</MenuItem>
                  <MenuItem value="high">Aukštas</MenuItem>
                  <MenuItem value="urgent">Skubus</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
          
          <TextField
            fullWidth
            label="Vieta (papildoma informacija)"
            value={newRequestForm.location}
            onChange={(e) => setNewRequestForm({ ...newRequestForm, location: e.target.value })}
            sx={{ mb: 2 }}
          />
          
          <TextField
            fullWidth
            label="Skyrius"
            value={newRequestForm.department}
            onChange={(e) => setNewRequestForm({ ...newRequestForm, department: e.target.value })}
            sx={{ mb: 2 }}
          />

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            📍 Koordinatės: X: {newRequestForm.mapCoordinates.x.toFixed(3)}, Y: {newRequestForm.mapCoordinates.y.toFixed(3)}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>
            Atšaukti
          </Button>
          <Button
            onClick={handleCreateRequest}
            variant="contained"
            disabled={!newRequestForm.title || !newRequestForm.description}
          >
            Sukurti Prašymą
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ticket Details Dialog */}
      <Dialog 
        open={ticketDetailsDialogOpen} 
        onClose={handleCloseTicketDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon color="primary" />
            <Typography variant="h6">
              Prašymo Informacija
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedTicket && (
            <Box sx={{ mt: 2 }}>
              {/* Title and Status */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" component="h2">
                  {selectedTicket.title}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip 
                    label={selectedTicket.status} 
                    color={getStatusColor(selectedTicket.status) as any}
                    size="small"
                  />
                  <Chip 
                    label={selectedTicket.priority} 
                    color={getPriorityColor(selectedTicket.priority) as any}
                    size="small"
                  />
                </Box>
              </Box>

              {/* Description */}
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedTicket.description}
              </Typography>

              {/* Details Grid */}
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                gap: 2, 
                mb: 2 
              }}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Kategorija
                  </Typography>
                  <Typography variant="body2">
                    {getCategoryLabel(selectedTicket.category)}
                  </Typography>
                </Paper>

                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Lokacija
                  </Typography>
                  <Typography variant="body2">
                    {selectedTicket.location || 'Nepasirinkta'}
                  </Typography>
                </Paper>

                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Skyrius
                  </Typography>
                  <Typography variant="body2">
                    {selectedTicket.department || 'Nepasirinkta'}
                  </Typography>
                </Paper>

                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Prašymo Data
                  </Typography>
                  <Typography variant="body2">
                    {new Date(selectedTicket.requestedDate).toLocaleDateString('lt-LT')}
                  </Typography>
                </Paper>
              </Box>

              {/* Requester Information */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Prašytojas
                </Typography>
                <Typography variant="body2">
                  {selectedTicket.requester.firstName} {selectedTicket.requester.lastName}
                </Typography>
              </Paper>

              {/* Map Coordinates */}
              {selectedTicket.mapCoordinates && (
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Žemėlapio Koordinatės
                  </Typography>
                  <Typography variant="body2">
                    X: {selectedTicket.mapCoordinates.x.toFixed(3)}, Y: {selectedTicket.mapCoordinates.y.toFixed(3)}
                  </Typography>
                </Paper>
              )}

              {/* Admin Notes */}
              {selectedTicket.adminNotes && (
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Administratoriaus Pastabos
                  </Typography>
                  <Typography variant="body2">
                    {selectedTicket.adminNotes}
                  </Typography>
                </Paper>
              )}

              {/* Cost Information */}
              {(selectedTicket.estimatedCost || selectedTicket.actualCost) && (
                <Paper sx={{ p: 2, mt: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Išlaidų Informacija
                  </Typography>
                  {selectedTicket.estimatedCost && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Numatomos išlaidos: €{selectedTicket.estimatedCost}
                    </Typography>
                  )}
                  {selectedTicket.actualCost && (
                    <Typography variant="body2">
                      Faktinės išlaidos: €{selectedTicket.actualCost}
                    </Typography>
                  )}
                </Paper>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTicketDetails}>
            Uždaryti
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InfrastructureMaps;
